import type { InfoCardProps } from '@stereoagency/ui';
import { defaultLanguage } from '@stereoagency/constants';
import { FiMapPin } from 'react-icons/fi';
import { buildFileUrl, parseAssetId } from '@sanity/asset-utils';
import flush from 'just-flush';
import { SyntheticEvent } from 'react';
import { job as jobSchema } from '@stereoagency/sanity-schema';
import { dateFormatterFactory, findInKeyArray } from '@/utils';
import { $globalSanityClient } from '@/sanity';

const queryType = jobSchema.schema.builder
  .pick(['_id', 'datetime', 'slug', 'title', 'description', 'location', 'pdf'])
  .map((resolver) => ({
    taxonomies: resolver.taxonomies.resolveIn(['slug', 'name']).use()
  }))
  .use();

export default function jobAsProps(
  job: (typeof queryType)[1][number],
  lang: string,
  onClick?: (slug: string) => void
): InfoCardProps | null {
  const title = job.title
    ? findInKeyArray(job.title, [lang, defaultLanguage.id])
    : null;

  const description = job.description
    ? findInKeyArray(job.description, [lang, defaultLanguage.id])
    : null;

  const pdf = job.pdf
    ? findInKeyArray(job.pdf, [lang, defaultLanguage.id])
    : null;

  if (!job.slug?.current || !job.datetime || !title?.value) {
    return null;
  }

  const dateFormatter = dateFormatterFactory(lang, {
    day: 'numeric',
    month: '2-digit',
    year: 'numeric'
  });

  const config = $globalSanityClient.config();
  const datetime = new Date(job.datetime);
  const metadata = job.location
    ? [{ icon: <FiMapPin />, label: job.location }]
    : [];
  const tags = flush(
    job.taxonomies?.map((tax) => {
      const slug = tax.slug?.current;
      const name = tax.name
        ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
        : null;
      if (!slug || !name?.value) return null;
      return {
        label: name.value,
        // @TODO add url
        href: '/',
        onClick(event: SyntheticEvent<HTMLElement>) {
          if (onClick) {
            event.preventDefault();
            onClick?.(slug);
          }
        }
      };
    }) ?? []
  );
  const fileUrlParts = pdf?.value?.asset._ref
    ? parseAssetId(pdf.value.asset._ref)
    : null;
  const actionUrl = fileUrlParts
    ? buildFileUrl({
        ...fileUrlParts,
        projectId: config.projectId,
        dataset: config.dataset
      })
    : null;

  return {
    metadata,
    tags,
    label: dateFormatter.format(datetime),
    title: title.value,
    description: description?.value,
    accentColor: '--colors-orange-700',
    action: { href: actionUrl ?? '/', target: '_blank' }
  };
}
